import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ClientSearch } from '@/components/clients/ClientSearch'
import { GuestFormWithPerson } from '@/components/guests/GuestFormWithPerson'
import { editReservaSchema } from '@/lib/schemas'
import { toast } from 'sonner'
import { type Reservation } from '@/services/reservations'
import { api } from '@/services/api'
import { IconUsers, IconTrash, IconUserPlus } from '@tabler/icons-react'
import { Loader2 } from 'lucide-react'
import { useUpdateReservation } from '@/hooks/useUpdateReservation'
import { GuestPortalSection } from './GuestPortalSection'

interface EditReservaModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
  reserva: Reservation
}

export function EditReservaModal({ open, onOpenChange, onSuccess, reserva }: EditReservaModalProps) {
  const [selectedClient, setSelectedClient] = useState<any>(null)
  const queryClient = useQueryClient()
  const updateReservationMutation = useUpdateReservation()

  const form = useForm({
    resolver: zodResolver(editReservaSchema),
    defaultValues: {
      client_id: '',
      data_entrada: '',
      data_sortida: '',
      num_adults: 1,
      num_nens: 0,
      preu_total: 0,
      deposit: 0,
      hora_arribada: '',
      notes_client: '',
      notes_internes: '',
      canal: 'DIRECT',
    },
  })

  // Fetch guests for this reservation
  const { data: guestsResponse, refetch: refetchGuests } = useQuery({
    queryKey: ['reservation-guests', reserva?.id],
    queryFn: async () => {
      const response = await api.get(`/viatgers?reserva_id=${reserva.id}`)
      return response.data
    },
    enabled: !!reserva?.id && open,
  })

  // Extract guests array from paginated response
  const guests = guestsResponse?.data || []

  // Fetch access token for this reservation
  const { data: accessTokens } = useQuery({
    queryKey: ['reservation-tokens', reserva?.id],
    queryFn: async () => {
      const response = await api.get(`/reservations/${reserva.id}`)
      return response.data?.accessTokens || []
    },
    enabled: !!reserva?.id && open,
  })

  const guestFormToken = accessTokens?.find((t: any) => t.tokenType === 'GUEST_FORM')

  // Delete guest mutation
  const deleteGuestMutation = useMutation({
    mutationFn: async (guestId: string) => {
      await api.delete(`/viatgers/${guestId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reservation-guests', reserva.id] })
      toast.success('Viatger eliminat correctament')
      refetchGuests()
    },
    onError: () => {
      toast.error('Error en eliminar el viatger')
    },
  })

  // Formatear fecha a YYYY-MM-DD
  const toYYYYMMDD = (date: string | Date | null | undefined): string => {
    if (!date) return ''
    try {
      const d = new Date(date)
      if (isNaN(d.getTime())) return ''
      return d.toISOString().split('T')[0]
    } catch (e) {
      return ''
    }
  }

  // Cargar datos de la reserva
  useEffect(() => {
    if (open && reserva) {
      // Normalizar canal
      let normalizedCanal = 'DIRECT'
      if (reserva.canal) {
        const canalUpper = reserva.canal.toUpperCase()
        if (canalUpper.includes('BOOKING')) normalizedCanal = 'BOOKING'
        else if (canalUpper.includes('AIRBNB')) normalizedCanal = 'AIRBNB'
        else if (canalUpper.includes('EXPEDIA')) normalizedCanal = 'EXPEDIA'
        else if (['DIRECT', 'BOOKING', 'AIRBNB', 'EXPEDIA', 'OTHER'].includes(reserva.canal)) {
          normalizedCanal = reserva.canal
        } else {
          normalizedCanal = 'OTHER'
        }
      }

      form.reset({
        client_id: reserva.client_id || '',
        data_entrada: toYYYYMMDD(reserva.data_entrada),
        data_sortida: toYYYYMMDD(reserva.data_sortida),
        num_adults: reserva.num_adults || 1,
        num_nens: reserva.num_nens || 0,
        preu_total: reserva.preu_total || 0,
        deposit: reserva.deposit || 0,
        hora_arribada: reserva.hora_arribada || '',
        notes_client: reserva.notes_client || '',
        notes_internes: reserva.notes_internes || '',
        canal: normalizedCanal,
      })

      // Si hay client, marcarlo como seleccionado
      if (reserva.client) {
        setSelectedClient(reserva.client)
      }
    }
  }, [open, reserva, form])

  const handleClientSelect = (client: any) => {
    console.log('Client selected:', client)
    setSelectedClient(client)
    form.setValue('client_id', client.id)

    // Obtener nombre para mostrar - usar estructura del backend
    const displayName = client.tipus_client === 'empresa'
      ? client.empresa || client.nom_complet
      : client.nom_complet || `${client.nom || ''} ${client.cognoms || ''}`.trim() || client.email

    toast.success(`Client seleccionat: ${displayName}`)
  }

  const onSubmit = async (data: any) => {
    const dataToSend: any = {
      data_entrada: data.data_entrada,
      data_sortida: data.data_sortida,
      num_adults: Number(data.num_adults),
      num_nens: Number(data.num_nens),
      preu_total: Number(data.preu_total),
      deposit: Number(data.deposit),
      canal: data.canal,
    }

    if (data.client_id) {
      dataToSend.client_id = data.client_id
    }

    if (data.hora_arribada) dataToSend.hora_arribada = data.hora_arribada
    if (data.notes_client) dataToSend.notes_client = data.notes_client
    if (data.notes_internes) dataToSend.notes_internes = data.notes_internes

    updateReservationMutation.mutate(
      { id: reserva.id, data: dataToSend },
      {
        onSuccess: () => {
          onSuccess()
          onOpenChange(false)
        },
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="full" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-2xl font-bold text-blue-700">
              Editar Reserva #{reserva?.beds24_id || reserva?.id}
            </DialogTitle>
            {/* Header badges */}
            <div className="flex items-center gap-2">
              {/* Door code badge */}
              {reserva?.beds24_id && (
                <span 
                  className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-mono font-bold bg-purple-100 text-purple-800 border border-purple-300"
                  title="Codi de porta (últims 4 dígits)"
                >
                  🔑 {reserva.beds24_id.slice(-4)}
                </span>
              )}
              {/* Guest count badge */}
              <span 
                className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold ${
                  guests.length >= (reserva?.num_adults + reserva?.num_nens || 1)
                    ? 'bg-green-100 text-green-800 border border-green-300'
                    : guests.length > 0
                      ? 'bg-yellow-100 text-yellow-800 border border-yellow-300'
                      : 'bg-red-100 text-red-800 border border-red-300'
                }`}
                title="Viatgers registrats / esperats"
              >
                <IconUsers className="h-4 w-4 mr-1.5" />
                {guests.length}/{reserva?.num_adults + reserva?.num_nens || '?'}
              </span>
            </div>
          </div>
          <DialogDescription>
            Modifica els detalls de la reserva i gestiona els viatgers
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">

            {/* Client Section */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <IconUsers className="h-5 w-5" />
                  Client de la Reserva
                </CardTitle>
                <CardDescription>
                  Busca i selecciona el client responsable de la reserva
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <ClientSearch
                  onSelect={handleClientSelect}
                  placeholder="Buscar client o empresa..."
                  value={selectedClient}
                />
                {selectedClient && (
                  <div className="p-3 bg-blue-50 rounded-md border border-blue-200">
                    <p className="text-sm font-medium text-blue-900">
                      Client actual: {selectedClient.tipus_client === 'empresa'
                        ? selectedClient.empresa || selectedClient.nom_complet
                        : selectedClient.nom_complet || `${selectedClient.nom || ''} ${selectedClient.cognoms || ''}`.trim() || selectedClient.email
                      }
                    </p>
                    {selectedClient.tipus_client === 'empresa' && (selectedClient.num_document || selectedClient.nif_empresa) && (
                      <p className="text-xs text-blue-700 mt-1">CIF: {selectedClient.num_document || selectedClient.nif_empresa}</p>
                    )}
                    {selectedClient.email && (
                      <p className="text-xs text-blue-700 mt-1">{selectedClient.email}</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Guests Section */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 justify-between">
                  <span className="flex items-center gap-2">
                    <IconUsers className="h-5 w-5" />
                    Viatgers ({guests?.length || 0})
                  </span>
                  <GuestFormWithPerson
                    defaultReservationId={reserva?.id}
                    onSuccess={() => refetchGuests()}
                    trigger={
                      <Button type="button" size="sm" variant="outline">
                        <IconUserPlus className="h-4 w-4 mr-2" />
                        Afegir Viatger
                      </Button>
                    }
                  />
                </CardTitle>
                <CardDescription>
                  Persones que s'allotjaran en aquesta reserva
                </CardDescription>
              </CardHeader>
              <CardContent>
                {guests && guests.length > 0 ? (
                  <div className="space-y-2">
                    {guests.map((guest: any) => {
                      // Prioritzar dades pròpies del guest, després person
                      const firstName = guest.nom || guest.person?.firstName || '';
                      const lastName = guest.cognoms || guest.person?.lastName || '';
                      const email = guest.email || guest.person?.email || '';
                      const docType = guest.tipus_document || guest.person?.documentType || '';
                      const docNum = guest.num_document || guest.person?.documentNumber || '';
                      
                      return (
                      <div
                        key={guest.id}
                        className="flex items-center justify-between p-3 border rounded-md hover:bg-gray-50"
                      >
                        <div>
                          <p className="font-medium">
                            {firstName} {lastName}
                          </p>
                          {email && (
                            <p className="text-sm text-gray-600">{email}</p>
                          )}
                          {docNum && (
                            <p className="text-xs text-gray-500">
                              {docType}: {docNum}
                            </p>
                          )}
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => {
                            if (confirm('Segur que vols eliminar aquest viatger?')) {
                              deleteGuestMutation.mutate(guest.id)
                            }
                          }}
                          disabled={deleteGuestMutation.isPending}
                        >
                          {deleteGuestMutation.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <IconTrash className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No hi ha viatgers assignats a aquesta reserva
                  </p>
                )}
              </CardContent>
            </Card>

            {/* GuestPortal Config Section */}
            <GuestPortalSection
              reservationId={reserva.id}
              guests={guests || []}
              accessToken={guestFormToken}
              totalGuests={reserva.total_hostes || reserva.num_adults || 2}
              onGuestsUpdate={refetchGuests}
            />

            <Separator />

            {/* Fechas */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="data_entrada"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Data d'Entrada *</FormLabel>
                    <FormControl>
                      <Input {...field} type="date" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="data_sortida"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Data de Sortida *</FormLabel>
                    <FormControl>
                      <Input {...field} type="date" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Ocupación */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="num_adults"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nombre d'Adults *</FormLabel>
                    <FormControl>
                      <Input {...field} type="number" min="1" onChange={e => field.onChange(parseInt(e.target.value) || 1)} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="num_nens"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nombre de Nens</FormLabel>
                    <FormControl>
                      <Input {...field} type="number" min="0" onChange={e => field.onChange(parseInt(e.target.value) || 0)} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="hora_arribada"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Hora d'Arribada</FormLabel>
                    <FormControl>
                      <Input {...field} type="time" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Precios */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="preu_total"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Preu Total (€)</FormLabel>
                    <FormControl>
                      <Input {...field} type="number" step="0.01" min="0" onChange={e => field.onChange(parseFloat(e.target.value) || 0)} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="deposit"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Dipòsit / Senyal (€)</FormLabel>
                    <FormControl>
                      <Input {...field} type="number" step="0.01" min="0" onChange={e => field.onChange(parseFloat(e.target.value) || 0)} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Canal */}
            <FormField
              control={form.control}
              name="canal"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Canal de Reserva</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="bg-white">
                      <SelectItem value="DIRECT">Directe</SelectItem>
                      <SelectItem value="BOOKING">Booking.com</SelectItem>
                      <SelectItem value="AIRBNB">Airbnb</SelectItem>
                      <SelectItem value="EXPEDIA">Expedia</SelectItem>
                      <SelectItem value="OTHER">Altres</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Notas */}
            <FormField
              control={form.control}
              name="notes_client"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes del Client</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={3} placeholder="Peticions especials, al·lèrgies, etc." />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes_internes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes Internes</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={2} placeholder="Notes només visibles per l'equip" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Botones */}
            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={updateReservationMutation.isPending}
              >
                Cancel·lar
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700"
                disabled={updateReservationMutation.isPending}
              >
                {updateReservationMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Actualitzant...
                  </>
                ) : (
                  'Actualitzar Reserva'
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
